import type { ReactElement } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
    MsalProvider,
    AuthenticatedTemplate,
    UnauthenticatedTemplate,
} from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Pages
import {
    LoginPage,
    Dashboard,
    TimeEntry,
    WeekView,
    MonthView,
    Projects,
    ProjectDetail,
    Approvals,
    Reports,
    Settings,
    Admin,
    Collab,
    AuthCallback,
    ClientPortal,
} from './pages';

// Components
import { Layout } from './components/Layout';

// Context
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/useAuth';
import { ThemeProvider } from './contexts/ThemeContext';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000,
            retry: 1,
        },
    },
});

interface AppProps {
    instance: PublicClientApplication;
}

function AuthenticatedRoutes() {
    const { isLoading, isAuthenticated, isManager, isAdmin, logout } = useAuth();

    // MSAL connaît le compte avant que l'API n'ait rendu son jeton de session. Rendre les
    // pages dès ce moment faisait partir leurs requêtes sans jeton : 401, puis relance.
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
                <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" aria-label="Connexion en cours" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] px-4">
                <div className="card max-w-md text-center space-y-4">
                    <p className="text-[var(--color-text)]">
                        Votre compte Microsoft est reconnu, mais l'ouverture de votre session CRA a échoué.
                    </p>
                    <button type="button" onClick={logout} className="btn-primary justify-center">
                        Se déconnecter
                    </button>
                </div>
            </div>
        );
    }

    // Les pages réservées renvoient au tableau de bord : l'API refuse de toute façon leurs
    // données, mais la page s'affichait vide ou en erreur, boutons de modification compris.
    const reserve = (autorise: boolean, page: ReactElement) => (autorise ? page : <Navigate to="/" replace />);

    return (
        <Layout>
            <Routes>
                {/* Dashboard */}
                <Route path="/" element={<Dashboard />} />

                {/* Time Entry */}
                <Route path="/time-entry" element={<TimeEntry />} />
                <Route path="/time-entry/:date" element={<TimeEntry />} />
                <Route path="/week" element={<WeekView />} />
                <Route path="/week/:date" element={<WeekView />} />
                <Route path="/month" element={<MonthView />} />
                <Route path="/month/:date" element={<MonthView />} />

                {/* Projects */}
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />

                {/* Approvals (managers) */}
                <Route path="/approvals" element={reserve(isManager, <Approvals />)} />

                {/* Reports */}
                <Route path="/reports" element={<Reports />} />

                {/* Settings */}
                <Route path="/settings" element={<Settings />} />

                {/* Admin */}
                <Route path="/admin" element={reserve(isAdmin, <Admin />)} />
                <Route path="/collab" element={reserve(isAdmin, <Collab />)} />

                {/* Client Portal - accessible to all users */}
                <Route path="/client-portal" element={<ClientPortal />} />

                {/* Auth callback */}
                <Route path="/auth/callback" element={<Navigate to="/" replace />} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Layout>
    );
}

function UnauthenticatedRoutes() {
    return (
        <Routes>
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="*" element={<LoginPage />} />
        </Routes>
    );
}

function App({ instance }: AppProps) {
    return (
        <ThemeProvider>
            <QueryClientProvider client={queryClient}>
                <MsalProvider instance={instance}>
                    <AuthProvider>
                        <BrowserRouter>
                            <AuthenticatedTemplate>
                                <AuthenticatedRoutes />
                            </AuthenticatedTemplate>
                            <UnauthenticatedTemplate>
                                <UnauthenticatedRoutes />
                            </UnauthenticatedTemplate>
                        </BrowserRouter>
                    </AuthProvider>
                </MsalProvider>
            </QueryClientProvider>
        </ThemeProvider>
    );
}

export default App;
